<script>
import { mapGetters } from 'vuex';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import QuoteTab from './quotes/QuoteTab.vue';
import DocumentsTab from './documents/DocumentsTab.vue';
import AppointmentsTab from './appointments/AppointmentsTab.vue';
import HotelTab from './hotel/HotelTab.vue';
import AgendamentoTab from './agendamento/AgendamentoTab.vue';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import {
  ROLES,
  QUOTE_PERMISSIONS,
  DOCUMENT_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
    QuoteTab,
    DocumentsTab,
    AppointmentsTab,
    HotelTab,
    AgendamentoTab,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { activeIndex: 0 };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
      getContact: 'contacts/getContact',
      currentUser: 'getCurrentUser',
      getCurrentAccountId: 'getCurrentAccountId',
      isModuleEnabled: 'accounts/isModuleEnabled',
    }),
    userPermissions() {
      return getUserPermissions(this.currentUser, this.getCurrentAccountId);
    },
    canManageQuotes() {
      return hasPermissions(
        [...ROLES, QUOTE_PERMISSIONS],
        this.userPermissions
      );
    },
    canManageDocuments() {
      return hasPermissions(
        [...ROLES, DOCUMENT_PERMISSIONS],
        this.userPermissions
      );
    },
    conversationDashboardApps() {
      return this.dashboardApps.filter(
        app => app.show_in_conversation !== false
      );
    },
    contact() {
      const contactId = this.currentChat?.meta?.sender?.id;
      return contactId ? this.getContact(contactId) : {};
    },
    dashboardAppTabs() {
      const accountId = this.getCurrentAccountId;
      const tabs = [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
      ];

      if (
        this.canManageQuotes &&
        this.isModuleEnabled(accountId, 'orcamentos')
      ) {
        tabs.push({
          key: 'quotes',
          index: tabs.length,
          name: this.$t('QUOTES.TITLE'),
        });
      }

      if (this.isModuleEnabled(accountId, 'hospedagem')) {
        tabs.push({
          key: 'hotel',
          index: tabs.length,
          name: this.$t('HOTEL_BOOKING.TITLE'),
        });
      }

      if (this.canManageDocuments) {
        tabs.push({
          key: 'documents',
          index: tabs.length,
          name: this.$t('DOCUMENTS.TITLE'),
        });
      }

      if (this.isModuleEnabled(accountId, 'compromissos')) {
        tabs.push({
          key: 'appointments',
          index: tabs.length,
          name: this.$t('APPOINTMENTS.TAB_TITLE'),
        });
      }

      if (this.isModuleEnabled(accountId, 'agendamentos')) {
        tabs.push({
          key: 'agendamento',
          index: tabs.length,
          name: 'Agendamento',
        });
      }

      this.conversationDashboardApps.forEach(dashboardApp => {
        tabs.push({
          key: `dashboard-${dashboardApp.id}`,
          index: tabs.length,
          name: dashboardApp.title,
        });
      });

      return tabs;
    },
    quotesTabIndex() {
      const tab = this.dashboardAppTabs.find(t => t.key === 'quotes');
      return tab ? tab.index : -1;
    },
    documentsTabIndex() {
      const tab = this.dashboardAppTabs.find(t => t.key === 'documents');
      return tab ? tab.index : -1;
    },
    hotelTabIndex() {
      const tab = this.dashboardAppTabs.find(t => t.key === 'hotel');
      return tab ? tab.index : -1;
    },
    appointmentsTabIndex() {
      const tab = this.dashboardAppTabs.find(t => t.key === 'appointments');
      return tab ? tab.index : -1;
    },
    agendamentoTabIndex() {
      const tab = this.dashboardAppTabs.find(t => t.key === 'agendamento');
      return tab ? tab.index : -1;
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', {
            inboxIds: [inboxId],
            includeAgentBots: true,
          });
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      :class="{
        'border-b border-b-n-weak !pt-2': !dashboardApps.length,
      }"
    />
    <woot-tabs
      v-if="currentChat.id"
      :index="activeIndex"
      class="h-10"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <div v-show="activeIndex === 0" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <div
      v-if="canManageQuotes && quotesTabIndex >= 0"
      v-show="activeIndex === quotesTabIndex"
      class="flex h-full min-h-0 m-0 overflow-auto"
    >
      <QuoteTab
        v-if="currentChat.id"
        :conversation-id="currentChat.id"
        :contact="contact"
        class="w-full"
      />
    </div>
    <div
      v-if="hotelTabIndex >= 0"
      v-show="activeIndex === hotelTabIndex"
      class="flex h-full min-h-0 m-0 overflow-auto"
    >
      <HotelTab
        v-if="currentChat.id"
        :conversation-id="currentChat.id"
        :contact="contact"
        class="w-full"
      />
    </div>
    <div
      v-if="canManageDocuments && documentsTabIndex >= 0"
      v-show="activeIndex === documentsTabIndex"
      class="flex h-full min-h-0 m-0 overflow-auto"
    >
      <DocumentsTab
        v-if="currentChat.id"
        :conversation-id="currentChat.id"
        :contact="contact"
        class="w-full"
      />
    </div>
    <div
      v-if="appointmentsTabIndex >= 0"
      v-show="activeIndex === appointmentsTabIndex"
      class="flex h-full min-h-0 m-0 overflow-auto"
    >
      <AppointmentsTab
        v-if="currentChat.id"
        :conversation-id="currentChat.id"
        :contact="contact"
        :inbox-id="currentChat.inbox_id"
        class="w-full"
      />
    </div>
    <div
      v-if="agendamentoTabIndex >= 0"
      v-show="activeIndex === agendamentoTabIndex"
      class="flex h-full min-h-0 m-0 overflow-auto"
    >
      <AgendamentoTab
        v-if="currentChat.id"
        :conversation-id="currentChat.id"
        :contact="contact"
        class="w-full"
      />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in conversationDashboardApps"
      v-show="
        dashboardAppTabs.find(t => t.key === `dashboard-${dashboardApp.id}`)
          ?.index === activeIndex
      "
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="
        dashboardAppTabs.find(t => t.key === `dashboard-${dashboardApp.id}`)
          ?.index === activeIndex
      "
      :config="dashboardApp.content"
      :send-user-data="dashboardApp.send_user_data"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
